/**
 * Swing Strategies Page (Redesigned)
 *
 * ML-based swing trading signals for leveraged ETFs (TQQQ, UPRO, SOXL).
 * New UX: Answer "Should I buy today?" within 2 seconds via HeroCard.
 */

import React, { useState, useEffect, useCallback } from 'react';
import useSwingSignals from '../hooks/useSwingSignals.js';
import HeroCard from '../components/Swing/HeroCard.jsx';
import StrategyDetails from '../components/Swing/StrategyDetails.jsx';
import TechnicalIndicators from '../components/Swing/TechnicalIndicators.jsx';
import './SwingStrategies.css';

// Supported tickers
const TICKERS = ['TQQQ', 'UPRO', 'SOXL'];

// API URL for swing signals
const SWING_API_URL = import.meta.env.VITE_SWING_API_URL || 'https://vectrax-swing-signals.thegpc806.workers.dev';

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

// Transaction Log Component (collapsed by default)
function TransactionLog({ trades, strategies }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState('balanced');

  const strategyTrades = trades[selectedStrategy] || [];

  if (Object.keys(trades).length === 0) {
    return null;
  }

  const displayTrades = expanded ? strategyTrades : strategyTrades.slice(0, 5);

  const STRATEGY_COLORS = {
    'conservative': '#4ade80',
    'balanced': '#60a5fa',
    'aggressive': '#f97316'
  };

  return (
    <div className="transaction-section">
      <div className="transaction-header-row" onClick={() => setExpanded(!expanded)}>
        <h3>Recent Trades</h3>
        <span className="expand-icon">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <>
          <div className="strategy-tabs">
            {['conservative', 'balanced', 'aggressive'].map((s) => (
              <button
                key={s}
                className={`tab-btn ${selectedStrategy === s ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedStrategy(s);
                }}
                style={{
                  borderBottomColor: selectedStrategy === s
                    ? STRATEGY_COLORS[s]
                    : 'transparent'
                }}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {strategyTrades.length === 0 ? (
            <div className="no-trades">No transaction history for this strategy</div>
          ) : (
            <>
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th>Entry</th>
                    <th>Price</th>
                    <th>Exit</th>
                    <th>Price</th>
                    <th>Return</th>
                    <th>Days</th>
                  </tr>
                </thead>
                <tbody>
                  {displayTrades.map((trade, idx) => (
                    <tr key={idx} className={trade.pct_return > 0 ? 'trade-win' : 'trade-loss'}>
                      <td>{formatDate(trade.entry_date)}</td>
                      <td>${trade.entry_price?.toFixed(2)}</td>
                      <td>{formatDate(trade.exit_date)}</td>
                      <td>${trade.exit_price?.toFixed(2)}</td>
                      <td className={trade.pct_return > 0 ? 'positive' : 'negative'}>
                        {trade.pct_return > 0 ? '+' : ''}{trade.pct_return?.toFixed(2)}%
                      </td>
                      <td>{trade.hold_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!expanded && strategyTrades.length > 5 && (
                <div className="show-more">
                  Show all {strategyTrades.length} trades
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function SwingStrategies() {
  const {
    ticker,
    data,
    loading,
    error,
    lastUpdated,
    refresh,
    changeTicker
  } = useSwingSignals('TQQQ');

  // Live data state
  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);

  // Fetch live data (includes intraday status)
  const fetchLiveData = useCallback(async () => {
    setLiveLoading(true);
    try {
      const response = await fetch(`${SWING_API_URL}?ticker=${ticker}&live=true`);
      if (response.ok) {
        const result = await response.json();
        setLiveData(result);
      }
    } catch (err) {
      console.error('Error fetching live data:', err);
    } finally {
      setLiveLoading(false);
    }
  }, [ticker]);

  // Fetch live data on mount and ticker change
  useEffect(() => {
    fetchLiveData();
  }, [fetchLiveData]);

  // Combined refresh
  const handleRefresh = useCallback(() => {
    refresh();
    fetchLiveData();
  }, [refresh, fetchLiveData]);

  const handleTickerChange = (newTicker) => {
    changeTicker(newTicker);
  };

  // Determine qualifying strategy from data
  const qualifyingStrategy = data?.strategies?.find(s => s.entry_today)?.strategy_key || null;

  // Merge data for HeroCard
  const heroData = data ? {
    ...data,
    daily_signal: liveData?.daily_signal || data.daily_signal,
    live_status: liveData?.live_status,
    unified_recommendation: liveData?.unified_recommendation || data.unified_recommendation
  } : null;

  return (
    <div className="swing-page">
      {/* Header */}
      <header className="swing-header">
        <div className="header-left">
          <h1>Swing Trading</h1>
          <p className="header-subtitle">ML-based bottom detection for leveraged ETFs</p>
        </div>
        <div className="header-right">
          <div className="last-updated">
            {lastUpdated && (
              <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
            )}
          </div>
        </div>
      </header>

      {/* Ticker Selector */}
      <div className="ticker-selector">
        {TICKERS.map((t) => (
          <button
            key={t}
            className={`ticker-btn ${ticker === t ? 'active' : ''}`}
            onClick={() => handleTickerChange(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <p>Error: {error}</p>
          <p>Make sure the swing signals API is available.</p>
        </div>
      )}

      {/* Loading State */}
      {loading && !data && (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading signals...</p>
        </div>
      )}

      {/* Main Content */}
      {data && (
        <>
          {/* Hero Card - The Answer */}
          <HeroCard
            ticker={ticker}
            data={heroData}
            dataAgeMinutes={liveData?.data_age_minutes}
            marketOpen={liveData?.market_open ?? false}
            onRefresh={handleRefresh}
            loading={loading || liveLoading}
          />

          {/* Collapsible Panels */}
          <div className="collapsible-panels">
            {/* Strategy Details */}
            <StrategyDetails
              strategies={data.strategies || []}
              qualifyingStrategy={qualifyingStrategy}
            />

            {/* Technical Indicators */}
            <TechnicalIndicators
              dailyData={data}
              intradayData={liveData?.live_status || {}}
              keyFeatures={data.key_features || {}}
            />
          </div>

          {/* Recent Trades (collapsed by default) */}
          {data.recent_trades && (
            <TransactionLog
              trades={data.recent_trades}
              strategies={data.strategies || []}
            />
          )}
        </>
      )}

      {/* Footer */}
      <footer className="swing-footer">
        <p>Strategy based on ML bottom detection trained on 2011-2024 data</p>
      </footer>
    </div>
  );
}
